import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { useWorkArea } from '@/contexts/WorkAreaContext';
import { createWorkArea, listCodebases } from '@/lib/api';
import type { CreateWorkAreaBody, WorkAreaListResponse, WorkAreaVisibility } from '@/lib/api';

function parseGithubProjectNumber(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : Number.NaN;
}

export function WorkAreaCreateDialog(): React.ReactElement {
  const queryClient = useQueryClient();
  const { setSelectedWorkAreaId } = useWorkArea();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [visibility, setVisibility] = useState<WorkAreaVisibility>('team');
  const [defaultHomeCodebaseId, setDefaultHomeCodebaseId] = useState('');
  const [githubProjectOwner, setGithubProjectOwner] = useState('');
  const [githubProjectNumber, setGithubProjectNumber] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data: codebases, isLoading: loadingCodebases } = useQuery({
    queryKey: ['codebases'],
    queryFn: listCodebases,
  });

  useEffect(() => {
    if (!open) {
      setName('');
      setDescription('');
      setVisibility('team');
      setDefaultHomeCodebaseId('');
      setGithubProjectOwner('');
      setGithubProjectNumber('');
      setError(null);
    }
  }, [open]);

  const sortedCodebases = useMemo(() => {
    const sorted = [...(codebases ?? [])];
    sorted.sort((a, b) => a.name.localeCompare(b.name));
    return sorted;
  }, [codebases]);

  const mutation = useMutation({
    mutationFn: (body: CreateWorkAreaBody) => createWorkArea(body),
    onSuccess: workArea => {
      queryClient.setQueryData<WorkAreaListResponse>(['workAreas'], current => ({
        workAreas: [
          workArea,
          ...(current?.workAreas.filter(existing => existing.id !== workArea.id) ?? []),
        ],
      }));
      void queryClient.invalidateQueries({ queryKey: ['workAreas'] });
      setSelectedWorkAreaId(workArea.id);
      setOpen(false);
    },
    onError: err => {
      setError(err instanceof Error ? err.message : 'Failed to create work area.');
    },
  });

  function handleSubmit(): void {
    setError(null);
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('Name is required.');
      return;
    }

    const owner = githubProjectOwner.trim();
    const parsedProjectNumber = parseGithubProjectNumber(githubProjectNumber);
    const hasOwner = owner.length > 0;
    const hasNumber = githubProjectNumber.trim().length > 0;
    if (hasOwner !== hasNumber) {
      setError('GitHub project owner and number must both be set or both be empty.');
      return;
    }
    if (Number.isNaN(parsedProjectNumber)) {
      setError('GitHub project number must be a positive whole number.');
      return;
    }

    mutation.mutate({
      name: trimmedName,
      description: description.trim() || null,
      visibility,
      default_home_codebase_id: defaultHomeCodebaseId || null,
      github_project_owner: hasOwner ? owner : null,
      github_project_number: parsedProjectNumber,
    });
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="inline-flex items-center gap-1.5">
          <Plus className="size-4" />
          New work area
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create work area</DialogTitle>
          <DialogDescription>
            Group codebases and artifacts around an active scope.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 pt-2">
          <label className="grid gap-1 text-sm">
            <span className="text-text-secondary">Name</span>
            <input
              type="text"
              value={name}
              onChange={e => {
                setName(e.target.value);
              }}
              className="rounded-md border border-border bg-surface px-2 py-1.5 text-sm text-text-primary"
            />
          </label>
          <label className="grid gap-1 text-sm">
            <span className="text-text-secondary">Description</span>
            <textarea
              value={description}
              onChange={e => {
                setDescription(e.target.value);
              }}
              rows={3}
              className="resize-none rounded-md border border-border bg-surface px-2 py-1.5 text-sm text-text-primary"
            />
          </label>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="grid gap-1 text-sm">
              <span className="text-text-secondary">Visibility</span>
              <select
                value={visibility}
                onChange={e => {
                  setVisibility(e.target.value as WorkAreaVisibility);
                }}
                className="rounded-md border border-border bg-surface px-2 py-1.5 text-sm text-text-primary"
              >
                <option value="team">team</option>
                <option value="private">private</option>
              </select>
            </label>
            <label className="grid gap-1 text-sm">
              <span className="text-text-secondary">Default codebase</span>
              <select
                value={defaultHomeCodebaseId}
                onChange={e => {
                  setDefaultHomeCodebaseId(e.target.value);
                }}
                disabled={loadingCodebases}
                className="rounded-md border border-border bg-surface px-2 py-1.5 text-sm text-text-primary disabled:opacity-60"
              >
                <option value="">None</option>
                {sortedCodebases.map(codebase => (
                  <option key={codebase.id} value={codebase.id}>
                    {codebase.github_repo_full_name ?? codebase.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <fieldset className="grid gap-3 rounded-md border border-border p-3">
            <legend className="px-1 text-sm font-medium text-text-secondary">
              Linked GitHub Project <span className="text-text-tertiary">(optional)</span>
            </legend>
            <p className="text-xs text-text-tertiary">
              Connect this Work Area to a GitHub Projects (v2) board. Metis ingests Project-item
              membership and surfaces it on the Workbench so the Work Area can pull in issues and
              cross-repo PRs already tracked there. Find both values in the Project URL:{' '}
              <code className="rounded bg-surface px-1">
                github.com/orgs/&lt;owner&gt;/projects/&lt;number&gt;
              </code>{' '}
              : owner is the organization or user login, number is the integer that follows. Both
              must be set together or both left blank.
            </p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_10rem]">
              <label className="grid gap-1 text-sm">
                <span className="text-text-secondary">GitHub project owner</span>
                <input
                  type="text"
                  value={githubProjectOwner}
                  onChange={e => {
                    setGithubProjectOwner(e.target.value);
                  }}
                  placeholder="droneup"
                  className="rounded-md border border-border bg-surface px-2 py-1.5 text-sm text-text-primary placeholder:text-text-tertiary"
                />
              </label>
              <label className="grid gap-1 text-sm">
                <span className="text-text-secondary">Project number</span>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={githubProjectNumber}
                  onChange={e => {
                    setGithubProjectNumber(e.target.value);
                  }}
                  placeholder="42"
                  className="rounded-md border border-border bg-surface px-2 py-1.5 text-sm text-text-primary placeholder:text-text-tertiary"
                />
              </label>
            </div>
          </fieldset>
          {error && <div className="rounded-md bg-error/15 p-2 text-sm text-error">{error}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={(): void => {
                setOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={mutation.isPending || !name.trim()}
            >
              {mutation.isPending ? 'Creating…' : 'Create work area'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
